'use client';
'use strict';

var _tslib = require('../../../_virtual/_tslib.js');
var jsxRuntime = require('react/jsx-runtime');
var react = require('react');
var tailwindMerge = require('tailwind-merge');
var Typography = require('../../Base/Typography/Typography.js');
var Box = require('../../Base/Layouts/Box/Box.js');
require('../../Base/Layouts/FullBleed/FullBleed.js');
var SystemIcon = require('../SystemIcon/SystemIcon.js');

const LabelContext = react.createContext({
    type: '',
    variant: '',
    size: ''
});
/**
 * @component Root
 * @description Label의 컨테이너 컴포넌트입니다. 레이블의 스타일과 레이아웃을 관리합니다.
 * @param {Object} props
 * @param {('text'|'outlined'|'filled')} [props.type] - 레이블의 표시 스타일 (text(default), outlined, filled)
 * @param {('primary'|'gray')} [props.variant] - 레이블의 색상 변형 (primary(default), gray)
 * @param {('sm'|'md')} [props.size] - 레이블의 크기 (sm(default), md)
 * @param {boolean} [props.canHover] - 호버 효과 활성화 여부 (false(default), true)
 */ const Root = (_a)=>{
    var { children, className, size = 'md', type = 'text', variant = 'primary', ref, canHover = false } = _a, rest = _tslib.__rest(_a, [
        "children",
        "className",
        "size",
        "type",
        "variant",
        "ref",
        "canHover"
    ]);
    const childrenArray = react.Children.toArray(children);
    const lastIndex = childrenArray.length - 1;
    const isIconElement = (element)=>{
        return react.isValidElement(element) && typeof element.type === 'function' && element.type.toString().includes('icon');
    };
    const hasLeadingIcon = isIconElement(childrenArray[0]);
    const hasTrailingIcon = isIconElement(childrenArray[lastIndex]);
    const generateContainerVariant = ()=>{
        const isOutlined = type === 'outlined';
        if (type === 'text') return;
        if (variant === 'gray') {
            if (isOutlined) {
                return canHover ? 'bg-w-gray-10 border-w-gray-200 hover:border-w-gray-400' : 'bg-w-gray-10 border-w-gray-200';
            }
            return canHover ? 'bg-w-gray-10 hover:bg-w-gray-200' : 'bg-w-gray-10';
        }
        if (isOutlined) {
            return canHover ? 'bg-primary-50 border-primary-200 hover:border-primary' : 'bg-primary-50 border-primary-200';
        }
        return canHover ? 'bg-primary-50 hover:bg-primary-100' : 'bg-primary-50';
    };
    const generateContainerStyle = ()=>{
        const isOutlined = type === 'outlined';
        const isSmall = size === 'sm';
        const baseStyle = 'rounded-lg px-2';
        if (type === 'text') return;
        if (isOutlined) return tailwindMerge.twMerge(baseStyle, isSmall ? 'border py-px' : 'border py-0', hasLeadingIcon && 'pl-1.5', hasTrailingIcon && 'pr-1.5');
        return tailwindMerge.twMerge(baseStyle, isSmall ? 'py-0.5' : 'py-px', hasLeadingIcon && 'pl-1.5', hasTrailingIcon && 'pr-1.5');
    };
    return jsxRuntime.jsx(LabelContext.Provider, {
        value: {
            type,
            variant,
            size
        },
        children: jsxRuntime.jsx(Box, Object.assign({
            "data-testid": "design-system-label",
            className: tailwindMerge.twMerge('flex w-fit items-center', size === 'sm' && 'gap-1', size === 'md' && 'gap-1.5', generateContainerStyle(), canHover && 'cursor-pointer', generateContainerVariant(), className)
        }, rest, {
            children: children
        }))
    });
};
/**
 * @component Icon
 * @description Label 내부에서 사용되는 아이콘 컴포넌트입니다.
 * @param {Object} props
 * @param {SystemIconName} [props.icon] - 시스템 아이콘 이름
 * @param {ReactNode} props.children - 텍스트 또는 아이콘을 포함한 자식 요소
 * @param {string} [props.className] - 추가적인 클래스 이름
 */ const Icon = ({ children, className, icon })=>{
    const { type, variant } = react.useContext(LabelContext);
    return jsxRuntime.jsxs(Box, {
        "data-testid": "design-system-label--icon",
        className: tailwindMerge.twMerge('aspect-w-[14px] size-[14px] shrink-0 object-contain', !icon && className),
        children: [
            icon && jsxRuntime.jsx(SystemIcon, {
                name: icon,
                className: tailwindMerge.twMerge('text-w-gray-400', variant === 'primary' && 'text-primary', type === 'text' && 'text-w-gray-400', className)
            }),
            children
        ]
    });
};
/**
 * @component Text
 * @description Label 내부의 텍스트를 표시하는 컴포넌트입니다.
 * @param {ReactNode} children - 텍스트 또는 아이콘을 포함한 자식 요소
 * @param {string} [className] - 추가적인 클래스 이름
 */ const Text = ({ children, className })=>{
    const { type, variant, size } = react.useContext(LabelContext);
    const isTypeText = type === 'text';
    return jsxRuntime.jsx(Typography, {
        variant: size === 'md' ? 'body2' : 'caption1',
        "data-testid": "design-system-label--text",
        className: tailwindMerge.twMerge('select-none text-w-gray-600', !isTypeText && variant === 'primary' && 'text-primary', className),
        children: children
    });
};
/**
 * @module Label
 * @description 레이블을 구성하는 컴포넌트들의 집합입니다.
 * @property {Component} Root - 레이블의 컨테이너 컴포넌트
 * @property {Component} Text - 레이블의 텍스트 컴포넌트
 * @property {Component} Icon - 레이블의 아이콘 컴포넌트
 * @example
 * // 기본 텍스트 레이블
 * <Label.Root>
 *   <Label.Text>Basic Label</Label.Text>
 * </Label.Root>
 *
 * // 아이콘이 있는 primary filled 레이블
 * <Label.Root type="filled" variant="primary">
 *   <Label.Icon icon="check" />
 *   <Label.Text>Status Complete</Label.Text>
 * </Label.Root>
 *
 * // 작은 크기의 회색 outlined 레이블
 * <Label.Root type="outlined" variant="gray" size="sm">
 *   <Label.Text>Draft</Label.Text>
 * </Label.Root>
 *
 * // 호버 효과가 있는 양쪽 아이콘 레이블
 * <Label.Root type="filled" canHover>
 *   <Label.Icon icon="calendar" />
 *   <Label.Text>Schedule</Label.Text>
 *   <Label.Icon icon="chevronDown" />
 * </Label.Root>
 */ const Label = {
    Root,
    Text,
    Icon
};

module.exports = Label;
